import { useMemo } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { compact, map, sortBy, uniq } from 'lodash-es'

import { type MessagingRailProps } from '@/components/organisms/MessagingRail'
import { type TeamConversationViewProps } from '@/components/templates/TeamConversationView'
import { eveningTeamsQuery } from '@/state/regions/regions'
import { teamMessagesQuery } from '@/state/teamMessages/teamMessages'
import { transportRequestsQuery } from '@/state/transportRequests/transportRequests'

import { teamCarType } from '../dashboard/data/teamCar'

import { messageTeamStatus, teamStatus } from './teamStatus'

export interface UseEveningMessagesPageOptions {
  eveningId: number
  teamId: string
  regionName: string
  piloteRegionId: number
  rail: MessagingRailProps
}

export const useEveningMessagesPage = ({
  eveningId,
  teamId,
  regionName,
  piloteRegionId,
  rail,
}: UseEveningMessagesPageOptions): TeamConversationViewProps => {
  const { data: teamsSnapshot } = useSuspenseQuery(eveningTeamsQuery(eveningId))

  const { data: messages } = useSuspenseQuery(teamMessagesQuery(teamId))

  const transportRequestIds = useMemo(() => uniq(compact(map(messages.data, 'transportRequestId'))), [messages.data])
  const { data: transportRequests } = useQuery(transportRequestsQuery(transportRequestIds))

  const teamItems = useMemo(
    () =>
      sortBy(
        teamsSnapshot.data.map((team) => ({
          _id: team._id,
          hasUnreadMessages: team.messagingStatus?.hasUnreadTeamMessages ?? false,
          carNumber: String(team.number ?? '?'),
          carType: teamCarType(team.vehicle),
          state: teamStatus(team),
          count: team.messagingStatus?.teamMessageCount ?? 0,
        })),
        (team) => Number(team.carNumber) || Infinity
      ),
    [teamsSnapshot.data]
  )

  const selectedTeam = useMemo(() => teamItems.find((team) => team._id === teamId), [teamItems, teamId])

  const messageItems = useMemo(
    () =>
      messages.data.map((message) => {
        const transportRequest = transportRequests?.data?.find((tr) => tr._id === message.transportRequestId)
        return {
          _id: message._id,
          sender: message.sender,
          senderName: message.senderName,
          volunteerRole: message.volunteerRole,
          createdAt: message.createdAt,
          readAt: message.readAt,
          text: message.text,
          firebaseStoragePath: message.firebaseStoragePath,
          imageHeight: message.imageProperties?.height,
          imageWidth: message.imageProperties?.width,
          audioStoragePath: message.audioStoragePath,
          audioDurationMs: message.audioDurationMs,
          isBroadcast: message.isBroadcast,
          car: selectedTeam && {
            state: messageTeamStatus(message),
            carNumber: selectedTeam.carNumber,
            carType: selectedTeam.carType,
          },
          transportRequest: transportRequest && {
            _id: transportRequest._id,
            piloteId: String(transportRequest.piloteId),
            status: message.transportRequestStatus,
            address:
              message.transportRequestStatus === 'allocated' || message.transportRequestStatus === 'assigned'
                ? transportRequest.origin?.formattedAddress
                : message.transportRequestStatus === 'on_board'
                  ? transportRequest.destination?.formattedAddress
                  : '',
          },
        }
      }),
    [messages.data, transportRequests?.data, selectedTeam]
  )

  return {
    rail,
    messages: messageItems,
    teams: teamItems,
    selectedTeam: teamId,
    regionName,
    piloteRegionId,
  }
}
